// Banner at the top of a Withdrawn or stage-Declined deal: the deal is
// read-only for everyone but ADMIN (Deal Pipeline spec §6.6; owner ruling,
// 2026-10-15). Pure presentation; WithdrawnOrDeclinedDealReadOnlyMiddleware
// enforces it. A ratified decline has DealDeclineBanner instead, whose lock
// (422) exempts ADMIN only for archive and restore (owner ruling, 2026-10-26).

import { Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { DEAL_FLOW_PERMISSIONS } from "@/lib/dealFlowPermissions";
import type { DealDetailDto } from "@/types/deals";

interface WithdrawnOrDeclinedDealBannerProps {
  deal: Pick<DealDetailDto, "stage" | "declineStatus">;
}

export function WithdrawnOrDeclinedDealBanner({ deal }: WithdrawnOrDeclinedDealBannerProps) {
  const { hasPermission } = useAuth();

  if (deal.stage !== "Withdrawn" && deal.stage !== "Declined") return null;
  if (deal.declineStatus === "Declined") return null;

  const word = deal.stage === "Withdrawn" ? "withdrawn" : "declined";
  const canEdit = hasPermission(DEAL_FLOW_PERMISSIONS.EditWithdrawnOrDeclinedDeal);

  return (
    <div
      className="border border-gold/40 bg-gold/5 rounded-md px-space-3 py-space-2 flex items-center gap-space-3"
      data-testid="withdrawn-or-declined-deal-banner"
      role="status"
    >
      <Lock size={18} className="text-gold-shadow shrink-0" aria-hidden />
      <p className="flex-1 min-w-0 text-small text-onyx m-0">
        {canEdit
          ? `This deal is ${word}. It is read-only for everyone except admins, so you can still edit it.`
          : `This deal is ${word}, so it is read-only: only an admin can edit it. It can still be cloned, archived or restored.`}
      </p>
    </div>
  );
}
